import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Checkbox, Col, Form, Input, Link, Row } from 'tdesign-react';
import { LockOnIcon, UserIcon } from 'tdesign-icons-react';
import { useMemoizedFn } from 'ahooks';
import { saveToken } from '@/slices/authSlice';
import { doLogin } from '@/services/authService';
import { changeFavico, isAscentstream } from '@/utils/common';
import { deleteItem, deleteToken, getItem, setItem, setToken } from '@/utils/storage';
import { fetchFrontConfig } from '@/slices/platformSlice';

const { FormItem } = Form;

function Login({ saveToken, frontConfig, fetchFrontConfig, loadingFrontConfig }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  // 隐藏的超级管理员登录页面
  const isSuper = location.pathname === '/superadmin-hlogin';
  const [showLoginPage, setShowLoginPage] = useState(isSuper);

  const account = getItem('account');

  const { 'third-party-org': thirdPartyOrg, 'third-party': thirdParty } = frontConfig || {};

  const doInit = useMemoizedFn(async () => {
    const { payload } = await fetchFrontConfig();
    const config = payload || {};
    // 迅速跳转到中信第三方登录,超管除外
    if (!isSuper && config['hide-login'] && thirdParty && config['third-party-org'] === 'citics') {
      window.location.href = config['third-party'];
      return;
    }
    setShowLoginPage(true);
  });

  const onLogin = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      const { rememberAccount, ...others } = fields;
      if (rememberAccount) {
        setItem('account', others.account);
      } else {
        deleteItem('account');
      }
      try {
        setLoading(true);
        deleteToken();
        const { data } = await doLogin(others);
        setToken(data.token);
        saveToken(data.token);
        navigate('/cluster');
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    doInit();
  }, [doInit]);

  useEffect(() => {
    changeFavico(frontConfig.favico);
    document.title = frontConfig.title;
  }, [frontConfig]);

  return (
    !loadingFrontConfig &&
    showLoginPage && (
      <Root>
        <Logo src={frontConfig['head-logo']} alt='logo' />
        <FormPanel>
          <Title>用户登录</Title>
          <Title>User Login</Title>
          <Form
            className='login-form'
            onSubmit={onLogin}
            initialData={{ account, rememberAccount: !!account }}
          >
            <FormItem name='account' rules={[{ required: true }]}>
              <Input
                size='large'
                placeholder='请输入用户名'
                autocomplete='new-password'
                prefixIcon={<UserIcon />}
                clearable
              />
            </FormItem>
            <FormItem name='password' rules={[{ required: true }]}>
              <Input
                size='large'
                placeholder='请输入密码'
                type='password'
                autocomplete='new-password'
                prefixIcon={<LockOnIcon />}
                clearable
              />
            </FormItem>
            <Row align='center' justify='space-between'>
              <Col>
                <FormItem name='rememberAccount'>
                  <Checkbox>记住账号</Checkbox>
                </FormItem>
              </Col>
              {thirdParty && thirdPartyOrg && thirdPartyOrg === 'citics' && (
                <Col>
                  <Link theme='primary' href={thirdParty} variant='outline'>
                    第三方登录
                  </Link>
                </Col>
              )}
            </Row>
            <LoginButton theme='primary' loading={loading} type='submit'>
              登录
            </LoginButton>
            {isAscentstream() && (
              <RegisterButton
                tag='a'
                href={`//crm.ascentstream.com/trial?from=${params.get('from') || 'console'}`}
                target='_blank'
                theme='default'
                variant='outline'
              >
                申请试用
              </RegisterButton>
            )}
          </Form>
        </FormPanel>
        <LeftImg src={require('./images/01@2x.png')} />
        <RightImg src={require('./images/02@2x.png')} />
      </Root>
    )
  );
}

export default connect(
  (state) => ({
    frontConfig: state.platform.frontConfig,
    loadingFrontConfig: state.platform.loadingFrontConfig,
  }),
  {
    saveToken,
    fetchFrontConfig,
  },
)(Login);

const Root = styled.div`
  background: #f7f8fa;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  .login-form {
    margin-top: 50px;
    display: flex;
    flex-direction: column;
  }
`;

const Logo = styled.img`
  position: absolute;
  left: 24px;
  top: 18px;
  z-index: 0;
  height: auto;
  width: auto;
  max-height: 40px;
  max-width: 200px;
  pointer-events: none;
`;

const FormPanel = styled.div`
  margin-left: 150px;
  width: 400px;
  display: flex;
  flex-direction: column;
  input:-webkit-autofill {
    transition: background-color 600000s 0s, color 600000s 0s;
  }
`;

const LoginButton = styled(Button)`
  height: 48px;
  margin-top: 44px;
  .t-button__text {
    font-size: 16px;
    font-weight: bold;
  }
`;

const RegisterButton = styled(LoginButton)`
  margin-top: 16px;
`;

const LeftImg = styled.img`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 0;
  width: ${(700 / 1440) * 100}%;
  height: auto;
  pointer-events: none;
`;

const RightImg = styled.img`
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 0;
  width: ${(752 / 1440) * 100}%;
  height: auto;
  pointer-events: none;
`;

const Title = styled.div`
  font-size: 36px;
  font-style: normal;
  font-weight: 600;
  line-height: 44px;
  color: var(--td-font-gray-1);
`;
